import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { ListGroup } from 'react-bootstrap';
import { getSubscriptionTier, TIERS } from '../../../../../util/subscription';
import instance from '../../../../../util/axios';

import NavIcon from '../NavIcon';
import NavBadge from '../NavBadge';

import { ConfigContext } from '../../../../../contexts/ConfigContext';
import * as actionType from '../../../../../store/actions';
import useWindowSize from '../../../../../hooks/useWindowSize';
import useAuth from '../../../../../hooks/useAuth';
import CommonModal from '../../../../../common/CommonModal';
import ModalFooter from '../../../../../common/ModalFooter';

const NavItem = ({ layout, item }) => {
  const windowSize = useWindowSize();
  const configContext = useContext(ConfigContext);
  const { dispatch } = configContext;
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (item.id !== 'supportTickets') return;
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const fetchUnread = async () => {
      try {
        const res = await instance.get('support-tickets/unread/count', { skipNotification: true });
        if (res?.data && typeof res.data.count === 'number') {
          setUnreadCount(res.data.count);
        } else if (res?.data && res.data.hasUnread) {
          setUnreadCount(res.data.count || 1);
        } else {
          setUnreadCount(0);
        }
      } catch (err) {
        // Silently ignore network failures
      }
    };

    fetchUnread();

    const handleRefresh = () => {
      fetchUnread();
    };
    window.addEventListener('refreshSupportTicketsCount', handleRefresh);
    return () => {
      window.removeEventListener('refreshSupportTicketsCount', handleRefresh);
    };
  }, [item.id, location.pathname]);
  const [upgradeMessage, setUpgradeMessage] = useState(
    'This feature is available on a higher subscription plan. Upgrade to Gold to unlock advanced analytics, filters, templates, and reporting features.'
  );

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logout();
    } catch (err) {
      console.error(err);
    } finally {
      setLogoutLoading(false);
    }
  };
  const isItemActive = () => {
    const path = location.pathname;
    // Standard match
    if (path === item.url) return true;

    // Special case for 'My Audit' / 'manageSites'
    if (item.id === 'manageSites') {
      return path.startsWith('/admin/manage-sites') || path.startsWith('/admin/site/');
    }

    // Special case for 'Manage Templates'
    if (item.id === 'manageTemplates') {
      return path.startsWith('/admin/manage-templates');
    }

    // Special case for 'My Chapters'
    if (item.id === 'myChapters') {
      return path.startsWith('/admin/my-chapters');
    }

    // Special case for 'Manage Delegate Role'
    if (item.id === 'manageDelegateRole') {
      return path.startsWith('/admin/manage-delegate-role');
    }

    // Default: match if the path starts with the item URL (for other nested routes)
    return path.startsWith(item.url);
  };

  let itemTitle = item.title;
  if (item.icon) {
    itemTitle = (
      <span className="pcoded-mtext">
        {item.title}
        {item.id === 'supportTickets' && unreadCount > 0 && (
          <span
            className="rounded-circle bg-danger ms-2"
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              verticalAlign: 'middle'
            }}
          />
        )}
      </span>
    );
  }

  let itemTarget = '';
  if (item.target) {
    itemTarget = '_blank';
  }

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userSubscription = userInfo?.subscription || 'Basic';
  const isSuperAdmin = userInfo?.role_name === 'SuperAdmin';
  const isGold = getSubscriptionTier(userSubscription) === TIERS.GOLD;
  const isBronze = getSubscriptionTier(userSubscription) === TIERS.BRONZE;
  const isMyChaptersRestricted = item.id === 'myChapters' && !isGold && !isSuperAdmin;
  const isDelegateRoleRestricted = item.id === 'manageDelegateRole' && isBronze;

  let subContent;
  if (item.external) {
    subContent = (
      <a href={item.url} target="_blank" rel="noopener noreferrer">
        <NavIcon items={item} />
        {itemTitle}
        <NavBadge items={item} />
      </a>
    );
  } else if (item.id === 'logout') {
    subContent = (
      <a
        href="#!"
        className="nav-link"
        onClick={(e) => {
          e.preventDefault();
          setShowLogoutModal(true);
        }}
      >
        <NavIcon items={item} />
        {itemTitle}
      </a>
    );
  } else if (isMyChaptersRestricted) {
    subContent = (
      <a
        href="#!"
        className="nav-link nav-link-locked"
        onClick={(e) => {
          e.preventDefault();
          setUpgradeMessage('Upgrade your subscription to access custom template and section management features.');
          setShowUpgradeModal(true);
        }}
        aria-label={`${item.title} (Upgrade to Gold to unlock)`}
      >
        <NavIcon items={item} />
        {itemTitle}
        <i className="feather icon-lock nav-lock-icon" aria-hidden="true" />
      </a>
    );
  } else if (isDelegateRoleRestricted) {
    subContent = (
      <a
        href="#!"
        className="nav-link nav-link-locked"
        onClick={(e) => {
          e.preventDefault();
          setUpgradeMessage('Upgrade your subscription to access Delegate Admin management features.');
          setShowUpgradeModal(true);
        }}
        aria-label={`${item.title} (Upgrade subscription to unlock)`}
      >
        <NavIcon items={item} />
        {itemTitle}
        <i className="feather icon-lock nav-lock-icon" aria-hidden="true" />
      </a>
    );
  } else {
    subContent = (
      <NavLink to={item.url} className={`nav-link ${isItemActive() ? 'active' : ''}`} target={itemTarget}>
        <NavIcon items={item} />
        {itemTitle}
        <NavBadge items={item} />
      </NavLink>
    );
  }
  let mainContent = '';
  if (layout === 'horizontal') {
    mainContent = (
      <ListGroup.Item as="li" bsPrefix=" " onClick={() => dispatch({ type: actionType.NAV_CONTENT_LEAVE })}>
        {subContent}
      </ListGroup.Item>
    );
  } else {
    if (windowSize.width < 992) {
      mainContent = (
        <ListGroup.Item as="li" bsPrefix=" " className={item.classes} onClick={() => dispatch({ type: actionType.COLLAPSE_MENU })}>
          {subContent}
        </ListGroup.Item>
      );
    } else {
      mainContent = (
        <ListGroup.Item as="li" bsPrefix=" " className={item.classes}>
          {subContent}
        </ListGroup.Item>
      );
    }
  }

  return (
    <React.Fragment>
      {mainContent}
      {item.id === 'logout' && (
        <CommonModal
          show={showLogoutModal}
          onHide={() => setShowLogoutModal(false)}
          title="Logout Account?"
          subtitle="Logging out of your account will end your current session on this device. You will need to sign in again to access your account and its features."
          type="logout"
          style={{ width: '600px' }}
        >
          <ModalFooter
            cancelLabel="Cancel"
            deleteLabel="Logout"
            onCancel={() => setShowLogoutModal(false)}
            onDelete={handleLogout}
            loading={logoutLoading}
            loadingLabel="Logging out..."
          />
        </CommonModal>
      )}

      {showUpgradeModal && (
        <CommonModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
          title="Upgrade Required"
          message={upgradeMessage}
          type="upgrade"
          disableFocusTrap={false}
        >
          <ModalFooter
            onCancel={() => setShowUpgradeModal(false)}
            onSubmit={() => {
              setShowUpgradeModal(false);
              navigate('/admin/subscription?tab=plans');
            }}
            submitLabel="Upgrade Plan"
            cancelLabel="Cancel"
            submitType="button"
          />
        </CommonModal>
      )}
    </React.Fragment>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  layout: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.string,
  target: PropTypes.string,
  external: PropTypes.bool,
  url: PropTypes.string,
  classes: PropTypes.string
};

export default NavItem;
